import { useEffect, useRef, useState } from 'react';
import { useThrottledCallback, type CallOptions } from 'use-debounce';

/**
 * Возвращает throttled-версию состояния.
 *
 * Частота обновления зависит от `delay`, а поведение на границах интервала
 * настраивается через `leading` и `trailing`.
 */
export function useThrottle<T>(
  value: T,
  delay: number,
  options?: CallOptions,
): T {
  const [throttledValue, setThrottledValue] = useState(value);
  const previousValueRef = useRef(value);

  const throttledSetValue = useThrottledCallback(
    setThrottledValue,
    delay,
    options,
  );

  useEffect(() => {
    if (Object.is(value, previousValueRef.current)) {
      return;
    }

    previousValueRef.current = value;
    throttledSetValue(value);
  }, [value, throttledSetValue]);

  useEffect(() => {
    return () => {
      throttledSetValue.cancel();
    };
  }, [throttledSetValue]);

  return throttledValue;
}
